/**
 * Grounding searches without rank tracking: the stored shape for reports
 * generated while grounding-search rank tracking is paused.
 *
 * Since 2026-09-25 Google serves invalid ("poisoned") result pages to rank
 * trackers, DataForSEO included (see `grounding-serp-tracking.ts`). Reports
 * generated while `isGroundingSerpTrackingEnabled()` is false run no Google or
 * Brave checks for their grounding searches, so they store this LIST instead of
 * the legacy gap analysis: the searches the AI platforms ran, how often, and on
 * which platforms. No ranks, no gaps, no competitor positions.
 *
 * The discriminator is the DATA SHAPE, not the report date. Anything stored in
 * `AiReport.groundingSearchRecommendations` without `version: 2` is a legacy
 * report and renders through the original rank-aware paths unchanged, so saved
 * reports keep showing exactly what they showed when they were generated.
 *
 * Browser-safe (no database import): the report UI, the PDF/PPT export and the
 * MCP markdown all branch on `isGroundingSearchListV2`, so it is re-exported
 * from `@repo/core/client`.
 */

export const GROUNDING_SEARCH_LIST_VERSION = 2 as const;

/**
 * The recommendation every no-rank report attaches to its grounding searches.
 * English only: the report's recommendations section and its exports are
 * English, and the tour carries its own translated copy.
 */
export const GROUNDING_SEARCH_CONTENT_STATEMENT =
	"Writing content that targets these searches is an important step toward getting cited by AI and, ultimately, included in its answers.";

/** One grounding search an AI platform ran while answering the report's prompts. */
export interface GroundingSearchListItem {
	groundingSearchId: string;
	query: string;
	/** Distinct report prompts whose answers ran this search. */
	usageCount: number;
	/** Platforms that ran it (e.g. "chatgpt", "gemini", "claude"), sorted. */
	platforms: string[];
	/** The report prompts that triggered it, in first-seen order. */
	discoveryQueries: Array<{
		queryId: string;
		query: string;
		queryType: string;
	}>;
}

export interface GroundingSearchListV2 {
	version: typeof GROUNDING_SEARCH_LIST_VERSION;
	/** Always false: this shape is only written when no SERP checks ran. */
	rankTracking: false;
	reportId: string;
	/** `searches.length`: the content-targetable searches. */
	totalSearches: number;
	/** Content-targetable searches, most used first (ties by query). */
	searches: GroundingSearchListItem[];
	/**
	 * `site:domain.com …` searches, kept apart as in the legacy shape: nobody can
	 * write content for someone else's site, but they show which sites the model
	 * already trusts.
	 */
	siteScopedSearches: GroundingSearchListItem[];
	/**
	 * Searches that name a tracked competitor without naming this brand
	 * ("CompetitorX pricing"): not something the brand can write for either.
	 */
	competitorNamedSearches: GroundingSearchListItem[];
}

/**
 * True for the no-rank (v2) shape. Everything else, including `null` and every
 * report stored before this shape existed, is legacy.
 */
export function isGroundingSearchListV2(
	value: unknown,
): value is GroundingSearchListV2 {
	if (!value || typeof value !== "object") return false;
	const v = value as Record<string, unknown>;
	return (
		v.version === GROUNDING_SEARCH_LIST_VERSION && Array.isArray(v.searches)
	);
}

/** Most used first, ties broken by query so the order is stable. */
export function sortGroundingSearchListItems<
	T extends Pick<GroundingSearchListItem, "usageCount" | "query">,
>(items: T[]): T[] {
	return [...items].sort(
		(a, b) => b.usageCount - a.usageCount || a.query.localeCompare(b.query),
	);
}
